import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, AlertTriangle, Merge, ArrowLeft } from "lucide-react";

export default function AthleteDuplicates() {
  const [clusters, setClusters] = useState(null);
  const [mergeCluster, setMergeCluster] = useState(null);

  const load = async () => {
    const { data } = await api.get("/athletes-duplicates").catch(() => ({ data: [] }));
    setClusters(data || []);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight flex items-center gap-3"><Users className="w-8 h-8 text-[#0052FF]" />Gestione duplicati</h1>
          <p className="text-zinc-500 mt-1"><Link to="/atleti" className="hover:underline"><ArrowLeft className="w-3 h-3 inline" /> Atleti</Link> · {clusters?.length ?? "…"} gruppi di possibili duplicati</p>
        </div>
      </div>

      {clusters === null && <div className="text-zinc-500">Analisi in corso…</div>}
      {clusters?.length === 0 && (
        <div className="bg-white border border-dashed border-zinc-300 rounded-md p-10 text-center">
          <Users className="w-8 h-8 text-zinc-300 mx-auto mb-3" />
          <div className="text-sm text-zinc-600">Nessun duplicato rilevato. Tutte le anagrafiche risultano distinte.</div>
        </div>
      )}
      {clusters?.map((cl, idx) => (
        <div key={idx} data-testid={`cluster-${idx}`} className="bg-white border border-zinc-200 rounded-md p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs uppercase tracking-wider text-zinc-500">Criterio: <span className="font-mono text-black">{cl.match_type}</span> · {cl.athletes.length} schede</div>
            <Button size="sm" data-testid={`open-merge-${idx}`} onClick={() => setMergeCluster(cl)} className="bg-black hover:bg-zinc-800"><Merge className="w-3.5 h-3.5 mr-1" />Unisci</Button>
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            {cl.athletes.map((a) => (
              <div key={a.id} className={`border rounded p-3 text-xs ${a.id === cl.suggested_primary_id ? "border-emerald-300 bg-emerald-50" : "border-zinc-200"}`}>
                <div className="font-medium text-sm">{a.first_name} {a.last_name}
                  {a.id === cl.suggested_primary_id && <span className="ml-2 text-[10px] uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded">Principale suggerita</span>}
                </div>
                <div className="text-zinc-600 font-mono">{a.birth_date || "—"} · {a.gender || "—"}</div>
                <div className="text-zinc-500">{a.email || "—"}</div>
                <div className="text-zinc-500">{a.phone || "—"}</div>
                <div className="text-[10px] text-zinc-400 font-mono mt-1">id: {a.id.slice(0, 8)}…</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {mergeCluster && <MergeDialog cluster={mergeCluster} onClose={() => setMergeCluster(null)} onDone={() => { setMergeCluster(null); load(); }} />}
    </div>
  );
}

function MergeDialog({ cluster, onClose, onDone }) {
  const [primaryId, setPrimaryId] = useState(cluster.suggested_primary_id);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const duplicates = cluster.athletes.filter((a) => a.id !== primaryId);

  const submit = async () => {
    if (!window.confirm(`Confermi l'unione di ${duplicates.length} schede in una?\nNessun record verrà cancellato: le presenze, test, gare e attività saranno trasferite alla scheda principale.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/athletes/merge", {
        primary_id: primaryId,
        duplicate_ids: duplicates.map((a) => a.id),
        reason: reason || null,
      });
      toast.success(`Unione completata: ${Object.values(data.transfers).reduce((s, n) => s + n, 0)} record trasferiti`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle>Unisci schede duplicate</DialogTitle>
          <DialogDescription>
            Seleziona la scheda principale. I record collegati (presenze, test, gare, attività, convocazioni, obiettivi, infortuni, armi, categorie) saranno trasferiti.
            Le schede duplicate saranno <strong>archiviate</strong> (soft delete) — non cancellate.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {cluster.athletes.map((a) => (
            <label key={a.id} className={`flex items-start gap-3 border rounded p-3 cursor-pointer ${primaryId === a.id ? "border-emerald-500 bg-emerald-50" : "border-zinc-200"}`}>
              <input type="radio" name="primary" checked={primaryId === a.id} onChange={() => setPrimaryId(a.id)} data-testid={`primary-${a.id}`} />
              <div className="text-sm flex-1">
                <div className="font-medium">{a.first_name} {a.last_name}</div>
                <div className="text-xs text-zinc-500 font-mono">{a.birth_date} · {a.email || "—"} · {a.phone || "—"}</div>
              </div>
            </label>
          ))}
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">Motivo (facoltativo)</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Es. stesso atleta con anagrafica errata" className="w-full h-9 border border-zinc-200 rounded px-3 text-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button data-testid="confirm-merge" onClick={submit} disabled={busy} className="bg-black hover:bg-zinc-800">{busy ? "Unione…" : `Unisci ${duplicates.length} schede`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
